/**
 * KoPartner Auth Modal
 * Flows:
 *   Register  → phone + email + password → /register → verify phone OTP → dashboard
 *   Login     → identifier + password    → /login    → dashboard
 *   Forgot PW → identifier               → /forgot-password → OTP → /reset-password → login
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Lock, Eye, EyeOff, Mail, Loader2, Globe } from 'lucide-react';
import axios from 'axios';

const API = '/api/v1';

// ── Country Codes ─────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+7', country: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+20', country: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: '+234', country: 'NG', name: 'Nigeria', flag: '🇳🇬' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, '');
  return digits;
};

const isValidPhone = (raw) => {
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// ✅ REMOVED: Password strength validation
// const isStrongPassword = (v) =>
//   /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(v);

const getErrorMessage = (err) => {
  if (!err.response) return 'Network error. Please check your connection.';
  const { status, data } = err.response;
  if (status === 429) {
    const retryAfter = data?.details?.retry_after;
    return retryAfter
      ? `Too many requests. Try again in ${Math.ceil(retryAfter / 60)} min.`
      : 'Too many requests. Please wait and try again.';
  }
  const errorCode = data?.error_code;
  if (errorCode === 'PHONE_EXISTS') return 'This phone number is already registered. Try logging in instead.';
  if (errorCode === 'EMAIL_EXISTS') return 'This email is already registered. Try logging in instead.';
  if (errorCode === 'INVALID_CREDENTIALS') return 'Invalid phone/email or password. If you are unable to login, please use "Forgot Password" to reset.';
  if (errorCode === 'ACCOUNT_DISABLED') return 'Your account has been disabled. Please contact support.';
  if (status === 422 && data?.detail) {
    const details = Array.isArray(data.detail) ? data.detail : [data.detail];
    const msgs = details.map(d => d.msg || d).filter(Boolean);
    if (msgs.length > 0) return msgs[0].replace('Value error, ', '');
  }
  return data?.message || data?.detail || 'Something went wrong. Please try again.';
};

// ✅ Web Device Info Collect
const getWebDeviceInfo = (selectedCountry) => {
  try {
    return {
      device_type: 'web',
      device_name: navigator.userAgent || 'Web Browser',
      device_id: null,
      brand: null,
      model: null,
      brand_model: null,
      os_version: navigator.platform || 'Unknown',
      app_version: null,
      build_number: null,
      locale: navigator.language || 'en-US',
      country_code: selectedCountry?.country || null,
      country_name: selectedCountry?.name || null,
      phone_country_code: selectedCountry?.code || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
      battery_level: null,
      is_charging: null,
      ip_address: null,
      fcm_token: null,
      last_active_at: new Date().toISOString()
    };
  } catch (e) {
    return {
      device_type: 'web',
      device_name: 'Web Browser',
      locale: 'en-US',
      timezone: 'Asia/Kolkata'
    };
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

const LoginModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('login');

  // Register fields
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regName, setRegName] = useState('');
  const [regCity, setRegCity] = useState('');
  const [regPincode, setRegPincode] = useState('');
  const [regGender, setRegGender] = useState('');
  const [regRole, setRegRole] = useState('client');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);

  // Login fields
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginMethod, setLoginMethod] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [loginOtp, setLoginOtp] = useState('');

  // Forgot / reset password
  const [fpIdentifier, setFpIdentifier] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Cooldown timer
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const reset = useCallback(() => {
    setStep('login');
    setRegPhone(''); setRegEmail(''); setRegPassword(''); setRegConfirm('');
    setLoginIdentifier(''); setLoginPassword('');
    setFpIdentifier(''); setFpOtp(''); setFpNewPassword('');
    setShowPassword(false); setShowConfirm(false);
    setError(''); setSuccess('');
    setOtpCooldown(0);
    setSelectedCountry(COUNTRY_CODES[0]);
  }, []);

  useEffect(() => { if (!isOpen) reset(); }, [isOpen, reset]);

  const handleClose = () => { reset(); onClose(); };

  // ── Register ────────────────────────────────────────────────────────────────

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    // ✅ Validate phone
    const rawPhone = regPhone.replace(/\D/g, '');
    if (rawPhone.length < 10 || rawPhone.length > 15) {
      setError('Enter a valid mobile number (10-15 digits)');
      return;
    }
    
    // ✅ Validate email
    if (!isValidEmail(regEmail)) {
      setError('Enter a valid email address');
      return;
    }
    
    // ✅ Validate password match
    if (regPassword !== regConfirm) {
      setError('Passwords do not match');
      return;
    }
    
    // ✅ REMOVED: Password strength validation
    // Password can be anything, minimum length 6
    if (regPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // ✅ Send phone WITHOUT country code
    const formattedPhone = rawPhone;
    
    console.log('📞 Raw Phone:', rawPhone);
    console.log('📞 Formatted Phone:', formattedPhone);

    const deviceInfo = getWebDeviceInfo(selectedCountry);

    // ✅ FIX: Sabhi cases me 'kopartner' bhej rahe hain
    // Client, Kopartner, Both - teeno me role_preference = 'kopartner'
    const finalRole = 'kopartner';

    const payload = {
      phone: formattedPhone,
      email: regEmail.trim(),
      password: regPassword,
      name: regName.trim() || undefined,
      city: regCity.trim() || undefined,
      pincode: regPincode.trim() || undefined,
      gender: regGender || undefined,
      role_preference: finalRole,  // ✅ Hamesha 'kopartner'
      device_type: deviceInfo.device_type,
      device_name: deviceInfo.device_name,
      os_version: deviceInfo.os_version,
      locale: deviceInfo.locale,
      country_code: deviceInfo.country_code,
      country_name: deviceInfo.country_name,
      phone_country_code: deviceInfo.phone_country_code,
      timezone: deviceInfo.timezone,
    };

    console.log('📤 Register Payload:', JSON.stringify(payload, null, 2));

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, payload);
      
      console.log('📥 Register Response:', res.data);
      
      const { access_token, refresh_token, payment, user, profile, kopartner_profile, documents } = res.data.data;
      
      const merged = {
        ...user,
        profile: profile || null,
        kopartner_profile: kopartner_profile || null,
        documents: documents || [],
        payment: payment || { has_payment: false, requires_payment: false }
      };
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('cached_user', JSON.stringify(merged));
      
      login(access_token, refresh_token, merged);
      handleClose();
      
      // ✅ Redirect logic
      if (payment?.requires_payment && payment?.redirect_to) {
        navigate(payment.redirect_to);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('❌ Registration error:', err.response?.data);
      console.error('❌ Status:', err.response?.status);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Login ───────────────────────────────────────────────────────────────────

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!loginIdentifier.trim()) { setError('Enter your phone number or email'); return; }
    if (!loginPassword) { setError('Enter your password'); return; }

    const identifier = isValidPhone(loginIdentifier)
      ? loginIdentifier.replace(/\D/g, '')
      : loginIdentifier.trim();

    const deviceInfo = getWebDeviceInfo(selectedCountry);

    const payload = {
      identifier,
      password: loginPassword,
      device_type: deviceInfo.device_type,
      device_name: deviceInfo.device_name,
      os_version: deviceInfo.os_version,
      locale: deviceInfo.locale,
      country_code: deviceInfo.country_code,
      country_name: deviceInfo.country_name,
      phone_country_code: deviceInfo.phone_country_code,
      timezone: deviceInfo.timezone,
    };

    console.log('📤 Login Payload:', payload);

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, payload);
      
      console.log('📥 Login Response:', res.data);
      
      const { access_token, refresh_token, payment, user, profile, kopartner_profile, documents } = res.data.data;
      
      const merged = {
        ...user,
        profile: profile || null,
        kopartner_profile: kopartner_profile || null,
        documents: documents || [],
        payment: payment || { has_payment: false, requires_payment: false }
      };
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('cached_user', JSON.stringify(merged));
      
      login(access_token, refresh_token, merged);
      handleClose();
      
      if (payment?.requires_payment && payment?.redirect_to) {
        navigate(payment.redirect_to);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Login with OTP ──────────────────────────────────────────────────────────

  const handleSendLoginOtp = async () => {
    setError('');
    if (!loginIdentifier.trim()) { setError('Enter your phone number'); return; }
    const phone = loginIdentifier.replace(/\D/g, '');

    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { value: phone, identifier_type: 'phone' });
      setOtpSent(true);
      setOtpCooldown(60);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!loginOtp || loginOtp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    const phone = loginIdentifier.replace(/\D/g, '');

    const deviceInfo = getWebDeviceInfo(selectedCountry);

    const payload = {
      phone,
      otp: loginOtp,
      device_type: deviceInfo.device_type,
      device_name: deviceInfo.device_name,
      os_version: deviceInfo.os_version,
      locale: deviceInfo.locale,
      country_code: deviceInfo.country_code,
      country_name: deviceInfo.country_name,
      phone_country_code: deviceInfo.phone_country_code,
      timezone: deviceInfo.timezone,
    };

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login-otp`, payload);
      
      const { access_token, refresh_token, payment, user, profile, kopartner_profile, documents } = res.data.data;
      
      const merged = {
        ...user,
        profile: profile || null,
        kopartner_profile: kopartner_profile || null,
        documents: documents || [],
        payment: payment || { has_payment: false, requires_payment: false }
      };
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('cached_user', JSON.stringify(merged));
      
      login(access_token, refresh_token, merged);
      handleClose();
      
      if (payment?.requires_payment && payment?.redirect_to) {
        navigate(payment.redirect_to);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ─────────────────────────────────────────────────────────

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!fpIdentifier.trim()) { setError('Enter your phone number or email'); return; }

    const identifier = isValidPhone(fpIdentifier)
      ? fpIdentifier.replace(/\D/g, '')
      : fpIdentifier.trim();

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { identifier });
      setSuccess('If an account exists, an OTP has been sent.');
      setStep('reset-password');
      setOtpCooldown(600);
    } catch (err) {
      setSuccess('If an account exists, an OTP has been sent.');
      setStep('reset-password');
      setOtpCooldown(600);
    } finally {
      setLoading(false);
    }
  };

  // ── Reset password ──────────────────────────────────────────────────────────

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (fpOtp.length !== 6 || !/^\d+$/.test(fpOtp)) {
      setError('Enter the 6-digit OTP');
      return;
    }
    
    // ✅ REMOVED: Password strength validation
    if (fpNewPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const identifier = isValidPhone(fpIdentifier)
      ? fpIdentifier.replace(/\D/g, '')
      : fpIdentifier.trim();

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        identifier,
        otp: fpOtp,
        new_password: fpNewPassword,
      });
      setSuccess('Password reset! You can now log in.');
      setTimeout(() => {
        setStep('login');
        setLoginIdentifier(fpIdentifier);
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold text-white">Welcome to KoPartner</h2>
          <p className="text-purple-100 mt-1">Your social & lifestyle support platform</p>
        </div>

        <div className="p-6">
          {/* Tab switcher */}
          {(step === 'login' || step === 'register') && (
            <div className="flex gap-2 mb-6">
              {[
                { key: 'login', label: 'Login' },
                { key: 'register', label: 'Register' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setStep(key); setError(''); setSuccess(''); }}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                    step === key
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* System update notice */}
          {(step === 'login') && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 text-sm">
              <p className="font-medium">🔄 System Updated</p>
              <p className="text-xs mt-1">If you're unable to login, please use "Forgot Password" to reset your password.</p>
            </div>
          )}

          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm font-medium">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm font-medium">
              {success}
            </div>
          )}

          {/* ── LOGIN ── */}
          {step === 'login' && (
            <div className="space-y-4">
              {/* Login method toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => { setLoginMethod('password'); setOtpSent(false); setLoginOtp(''); setError(''); }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition ${loginMethod === 'password' ? 'bg-white shadow text-purple-700' : 'text-gray-500'}`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => { setLoginMethod('otp'); setError(''); }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition ${loginMethod === 'otp' ? 'bg-white shadow text-purple-700' : 'text-gray-500'}`}
                >
                  Login with OTP
                </button>
              </div>

              {/* Password login */}
              {loginMethod === 'password' && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone or Email</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={loginIdentifier}
                        onChange={(e) => setLoginIdentifier(e.target.value)}
                        placeholder="Mobile number or email"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        autoComplete="username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Your password"
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <div className="text-right mt-1">
                      <button
                        type="button"
                        onClick={() => { setStep('forgot-password'); setError(''); }}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Loader2 className="animate-spin" size={18} /> Logging in...</> : 'Login'}
                  </button>
                </form>
              )}

              {/* OTP login */}
              {loginMethod === 'otp' && (
                <form onSubmit={handleLoginOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={loginIdentifier}
                        onChange={(e) => { setLoginIdentifier(e.target.value); setOtpSent(false); }}
                        placeholder="Enter your mobile number"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        disabled={otpSent}
                      />
                    </div>
                  </div>

                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={handleSendLoginOtp}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : 'Send OTP'}
                    </button>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Enter OTP</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={loginOtp}
                          onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="6-digit OTP"
                          maxLength={6}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-xl tracking-widest font-mono focus:ring-2 focus:ring-purple-500"
                          autoFocus
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading || loginOtp.length !== 6}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {loading ? <><Loader2 className="animate-spin" size={18} /> Verifying...</> : 'Verify & Login'}
                      </button>
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={handleSendLoginOtp}
                          disabled={otpCooldown > 0 || loading}
                          className="text-sm text-purple-600 hover:text-purple-700 disabled:opacity-50"
                        >
                          {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend OTP'}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          )}

          {/* ── REGISTER ── */}
          {step === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Country Code Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <select
                    value={selectedCountry.code}
                    onChange={(e) => {
                      const country = COUNTRY_CODES.find(c => c.code === e.target.value);
                      setSelectedCountry(country || COUNTRY_CODES[0]);
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white appearance-none"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                    {selectedCountry.code}
                  </span>
                  <input
                    type="tel"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="Enter mobile number"
                    className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {selectedCountry.flag} {selectedCountry.name} (10 digit number only)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Enter your password (min 6 characters)"
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={regConfirm}
                    onChange={(e) => setRegConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    className="w-full px-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {regConfirm && regPassword !== regConfirm && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={regCity}
                    onChange={(e) => setRegCity(e.target.value)}
                    placeholder="Your city"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    value={regPincode}
                    onChange={(e) => setRegPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit"
                    maxLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={regGender}
                  onChange={(e) => setRegGender(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* ✅ 3 Options - Find, Become, Both (Sabhi KoPartner banenge) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">I want to <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'client', label: 'Find a KoPartner' },
                    { value: 'kopartner', label: 'Become a KoPartner' },
                    { value: 'both', label: 'Both' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRegRole(opt.value)}
                      className={`py-2.5 px-2 rounded-lg text-xs font-medium border-2 transition ${
                        regRole === opt.value
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 text-gray-600 hover:border-purple-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Note: All options will create a KoPartner account.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Creating account...</> : 'Create Account'}
              </button>
            </form>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {step === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Forgot Password?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Enter your phone or email — we'll send an OTP
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone or Email
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={fpIdentifier}
                    onChange={(e) => setFpIdentifier(e.target.value)}
                    placeholder="Mobile number or email"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !fpIdentifier.trim()}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending OTP...</> : 'Send OTP'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setStep('login'); setError(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}

          {/* ── RESET PASSWORD ── */}
          {step === 'reset-password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="text-center mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Reset Password</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Enter the OTP sent to <strong>{fpIdentifier}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OTP</label>
                <input
                  type="text"
                  value={fpOtp}
                  onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit OTP"
                  className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={fpNewPassword}
                    onChange={(e) => setFpNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minimum 6 characters</p>
              </div>

              <button
                type="submit"
                disabled={loading || fpOtp.length !== 6 || !fpNewPassword}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Resetting...</> : 'Reset Password'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep('forgot-password'); setFpOtp(''); setError(''); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ← Change identifier
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('forgot-password'); handleForgotPassword({ preventDefault: () => {} }); }}
                  disabled={otpCooldown > 0 || loading}
                  className="text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                >
                  {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <p className="text-xs text-gray-400 text-center mt-6">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-purple-600 hover:underline">Terms</a> and{' '}
            <a href="/privacy" className="text-purple-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
